// @givmo/assistant-panel — the composer, and the two things that sit above it.
//
// ══ THE PROMPTS PERSIST (D-EP-40) ══════════════════════════════════════════════════════════════
//
// The suggested prompts do NOT disappear once the thread has messages. They are how a person can see
// that the assistant's scope follows the page — the same conversation shows different prompts on
// different surfaces — and that is only visible if they are still there after the first question.
//
// ══ THE CONTEXT CHIP ═══════════════════════════════════════════════════════════════════════════
//
// It names the page, using that page's OWN glyph. Matching the highlighted navigation row is what
// makes it legible without a "Context:" label, and it lives INSIDE the composer's border because it
// describes what the next message will be about rather than what the panel is.
//
// ══ WHY THE COMPOSER OCCUPIES THE FOOTER AS A COLUMN ═══════════════════════════════════════════
//
// `Sheet.Footer` is a trailing-aligned action row by default. Here it holds a column — chips over
// input — because the composer is the panel's permanent working area, not its actions. The region's
// own border/padding recipe is untouched; only the axis and alignment are overridden, so the panel
// keeps the same bottom edge every other Sheet in the suite has.

import { useCallback, useEffect, type KeyboardEvent, type RefObject } from 'react';
import { Icon, testIdProp } from '@givmo/design-system';
import { ArrowUp, Square } from 'lucide-react';
import type { AssistantSurface, AssistantVocab } from './assistant-panel-config';

export interface AssistantComposerProps {
  /**
   * The input element itself, owned by the panel so `Sheet.Content`'s `initialFocus` can name it.
   *
   * ⚠ Focus-on-open goes through `initialFocus` rather than an `autoFocus` effect here, and the two
   * must not both exist: Base UI focuses the first tabbable element when the panel opens (the header
   * close button), so a local effect would be racing it — sometimes winning, sometimes not, and never
   * the same way twice.
   */
  inputRef: RefObject<HTMLTextAreaElement | null>;
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  onStop: () => void;
  /** A reply is arriving — the send control becomes a stop control. */
  streaming: boolean;
  /** The page the next message is about, or `null` when the host's id names none. */
  surface: AssistantSurface | null;
  /** Whether the composer accepts input at all (a consent refusal closes it). */
  disabled: boolean;
  vocab: AssistantVocab;
  testId: string;
}

/** How tall the input may grow before it scrolls. Four lines of body text, matching the composer's
 *  own line height — past that a person is writing a document, not a message. */
const MAX_INPUT_HEIGHT_PX = 132;

export function AssistantComposer({
  inputRef,
  value,
  onChange,
  onSend,
  onStop,
  streaming,
  surface,
  disabled,
  vocab,
  testId,
}: AssistantComposerProps) {
  // Grow with the text, up to the cap. Reset to `auto` first: `scrollHeight` on an element that is
  // still holding its previous explicit height never shrinks, so a deleted line would leave the box
  // permanently tall.
  useEffect(() => {
    const input = inputRef.current;
    if (input === null) return;
    input.style.height = 'auto';
    input.style.height = `${Math.min(input.scrollHeight, MAX_INPUT_HEIGHT_PX)}px`;
  }, [value, inputRef]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      // Enter sends; Shift+Enter is a newline. The composer is a message box first and a text area
      // second, so the unmodified key does the common thing.
      if (event.key !== 'Enter' || event.shiftKey) return;
      event.preventDefault();
      onSend();
    },
    [onSend],
  );

  const canSend = value.trim().length > 0 && !streaming && !disabled;

  return (
    <div className="flex w-full flex-col">
      {surface !== null && surface.prompts.length > 0 && (
        <div className="mb-10 flex flex-wrap gap-6" {...testIdProp(`${testId}-prompts`)}>
          {surface.prompts.map((prompt) => (
            <button
              key={prompt}
              type="button"
              disabled={disabled || streaming}
              onClick={() => onChange(prompt)}
              className="inline-flex h-28 items-center rounded-md border border-border bg-surface px-10 font-sans text-s12 font-w500 tracking-givmo text-text-secondary outline-none transition-colors hover:bg-surface-hover hover:text-text-strong focus-visible:shadow-ring disabled:opacity-50"
              {...testIdProp(`${testId}-prompt`)}
            >
              {prompt}
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-10 rounded-md border border-neutral-500 bg-surface p-12 focus-within:border-brand-500 focus-within:shadow-ring">
        {surface !== null && (
          <span
            className="inline-flex h-20 items-center gap-5 self-start rounded-all6 bg-neutral-100 px-6 font-sans text-s11 font-w600 tracking-normal text-text-secondary"
            {...testIdProp(`${testId}-context-${surface.id}`)}
          >
            <Icon icon={surface.icon} size={12} className="text-icon" aria-hidden />
            {surface.label}
          </span>
        )}
        <div className="flex items-end gap-10">
          <textarea
            ref={inputRef}
            rows={1}
            value={value}
            disabled={disabled}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={vocab.composerPlaceholder}
            aria-label={vocab.composerPlaceholder}
            className="min-h-22 flex-1 resize-none bg-transparent font-sans text-s14 leading-h15 tracking-givmo text-text outline-none placeholder:text-text-placeholder disabled:cursor-not-allowed"
            {...testIdProp(`${testId}-input`)}
          />
          {streaming ? (
            <button
              type="button"
              onClick={onStop}
              aria-label={vocab.stopLabel}
              className="inline-flex size-28 shrink-0 items-center justify-center rounded-md border border-border-strong bg-surface text-text-secondary outline-none transition-colors hover:bg-surface-hover focus-visible:shadow-ring"
              {...testIdProp(`${testId}-stop`)}
            >
              <Icon icon={Square} size={11} className="fill-current" aria-hidden />
            </button>
          ) : (
            <button
              type="button"
              onClick={onSend}
              disabled={!canSend}
              aria-label={vocab.sendLabel}
              className={[
                'inline-flex size-28 shrink-0 items-center justify-center rounded-md outline-none transition-colors focus-visible:shadow-ring',
                canSend
                  ? 'bg-brand-600 text-neutral-0 hover:bg-brand-700'
                  : 'bg-neutral-100 text-neutral-400',
              ].join(' ')}
              {...testIdProp(`${testId}-send`)}
            >
              <Icon icon={ArrowUp} size={15} aria-hidden />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
